/**
 * Tolerant matching for planner-entered free-text professional roles (s118 / s74).
 *
 * Planners type the `role` on a Professional Contacts row freely ("Estate Lawyer",
 * "Estate lawyer – Smith LLP", "Lawyer", "Notary / Commissioner", …). Consumers that
 * pull a specific professional (probate document generator, testator review) must not
 * silently blank on a variant. This is the shared, reusable normalized matcher those
 * consumers use instead of exact-string comparison.
 *
 * NOTE (Pass-2 follow-up): the essentials evaluator (`shared/essentials-spec.ts`,
 * `estate_lawyer` op "in") still does its own `raw === term` exact-equals and therefore
 * fails on suffixed roles ("Estate Lawyer – Smith LLP"). Converging it onto this
 * primitive is logged as a deferred item — deliberately NOT changed here to avoid moving
 * planner-side completion behavior during the interim patch.
 */

/** Lowercase → collapse internal whitespace → trim. `null`/`undefined` → "". */
export function normalizeRole(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * True when the normalized `value` equals OR contains any normalized accepted term.
 * (Equality is subsumed by `includes`, but the intent is "equals-or-contains".)
 */
export function matchesRole(
  value: string | null | undefined,
  acceptedTerms: readonly string[],
): boolean {
  const v = normalizeRole(value);
  if (!v) return false;
  return acceptedTerms.some((term) => {
    const t = normalizeRole(term);
    return t !== "" && v.includes(t);
  });
}

/**
 * Accepted terms for the single "legal contact" used by the probate document generator.
 * Mirrors the s95 essential broadening (estate lawyer + notary) and adds bare "lawyer"
 * per s118 D-B. "estate lawyer" is kept explicit for readability though "lawyer" subsumes it.
 */
export const LEGAL_CONTACT_ROLE_TERMS = ["estate lawyer", "lawyer", "notary"] as const;

/**
 * Per-bucket accepted terms for the Testator Review professionals list.
 *
 * Semantics (a): a single role MAY match multiple buckets — the walkthrough shows the
 * person in each matching slot (e.g. "Lawyer & Notary" fills both). Terms are chosen to
 * avoid *accidental* overlap: Financial Advisor uses the full phrase (not bare "advisor"),
 * so "Insurance Advisor" lands only in the Insurance bucket.
 */
export const REVIEW_PROFESSIONAL_ROLE_TERMS: Record<string, readonly string[]> = {
  "Estate Lawyer": ["estate lawyer", "lawyer"],
  "Notary / Commissioner": ["notary", "commissioner"],
  "Accountant / Tax Preparer": ["accountant", "tax preparer", "cpa"],
  "Financial Advisor": ["financial advisor", "financial adviser"],
  "Insurance Agent / Broker": ["insurance"],
  // s128 (BINDING-AUDIT-1 Tranche 2, Rider 1B): the Professional Contacts "Real Estate Agent"
  // role option; bound by D("Real Estate Agent") on sell-transfer-real-property. Terms are
  // disjoint from the other buckets (no accidental overlap).
  "Real Estate Agent": ["real estate", "realtor"],
};

/**
 * s126 (LETTERS-GROUP-A, retires LD-1) — tolerant employment-status buckets for selecting
 * the active-vs-retired employer notification template per Employment History row. Replaces
 * the brittle exact-string gate (`status !== "Active Employee"`) in `server/letter-data.ts`
 * so a label drift ("Active Employee" → "Active" / "Employed") still selects correctly. The
 * canonical option set is ["Active Employee", "Retired / Former"]; a blank status still emits
 * BOTH letters (handled by the caller), and a non-blank status matching NEITHER bucket emits
 * neither (preserves the pre-s126 behavior). Buckets are disjoint on the canonical values.
 */
export const EMPLOYMENT_STATUS_TERMS: Record<"active" | "retired", readonly string[]> = {
  active: ["active", "current", "employed", "employee"],
  retired: ["retired", "former", "pension"],
};
